<script setup lang="ts">
/*
 FILE: unified_ui/src/components/DevTestLogin.vue
 PURPOSE: Password form shown only when the URL has `?devtest` and webBridge
          confirms a devtest key hash is installed. Exchanges the operator key
          for an isolated `web/devtest` session (the key itself is never
          stored). Rendered instead of AuthGate until login succeeds.
 OWNS: devtest login UI.
 EXPORTS: DevTestLogin (no props/emits; visibility is driven by the identity store)
 DOCS: docs/ops/devtest-access.md
*/
import { computed, ref } from 'vue'
import { useIdentityStore } from '@/stores/identity'

const identity = useIdentityStore()
const key = ref('')

const errorText = computed(() => {
  switch (identity.devTestError) {
    case 'invalid_key': return 'Неверный ключ.'
    case 'too_many_attempts': return 'Слишком много попыток. Подождите несколько минут.'
    case 'login_failed': return 'Сервер не смог создать тестовую сессию. Попробуйте ещё раз.'
    default: return ''
  }
})

async function submit() {
  const value = key.value.trim()
  if (!value || identity.devTestChecking) return
  const ok = await identity.loginDevTest(value)
  if (ok) key.value = ''
}

function useNormalAuth() {
  window.location.assign(window.location.pathname + window.location.hash)
}
</script>

<template>
  <div
    class="flex min-h-screen w-full items-center justify-center bg-gradient-to-b from-[#e8f0f8] to-[#f0f4f8] p-6 text-slate-900 dark:from-[#0b1120] dark:to-[#131c31] dark:text-slate-100"
    data-testid="devtest-login"
  >
    <div class="card-heavy w-full max-w-md p-6 md:p-8">
      <div class="flex items-center gap-2">
        <span class="rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          dev test
        </span>
        <h1 class="text-xl font-semibold">Тестовый вход</h1>
      </div>

      <p class="mt-3 text-sm leading-relaxed text-ink-600 dark:text-slate-400">
        Введите ключ оператора, чтобы открыть production-интерфейс под изолированным
        тестовым пользователем.
      </p>

      <form class="mt-6 space-y-3" @submit.prevent="submit">
        <input
          v-model="key"
          type="password"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          class="w-full rounded-xl border border-ink-200 bg-white px-4 py-3 font-mono text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-100 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-brand-900/40"
          placeholder="96-символьный ключ"
          data-testid="devtest-key"
          :disabled="identity.devTestChecking"
        >
        <button
          type="submit"
          class="btn-primary w-full"
          :disabled="identity.devTestChecking || key.trim().length === 0"
          data-testid="devtest-submit"
        >
          {{ identity.devTestChecking ? 'Проверяю…' : 'Войти' }}
        </button>
      </form>

      <p v-if="errorText" class="mt-3 text-sm text-red-600 dark:text-red-400" data-testid="devtest-error">
        {{ errorText }}
      </p>

      <p class="mt-6 text-xs leading-relaxed text-ink-500 dark:text-slate-400">
        Сессия живёт 12 часов. Заявки, уведомления менеджеру и оценки отключены —
        доступны чат, загрузка файлов и рабочее окружение агента. Ключ не
        сохраняется в браузере.
      </p>

      <button
        type="button"
        class="mt-4 text-xs font-medium text-ink-500 underline-offset-2 hover:underline dark:text-slate-400"
        @click="useNormalAuth"
      >
        Обычный вход
      </button>
    </div>
  </div>
</template>
